import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Tabs,
  Tab,
  Autocomplete,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";
import SupplierSearchSelectAsync from "../SupplierSearchSelectAsync";
import { buildSourceExternalKey, parseSourceExternalKey } from "../../utils/sourceExternalKey";

export default function QboVendorLinkDialog({
  open,
  onClose,
  instanceUniqueId,
  partyType = "supplier",
  partyId = null,
  partyName = "",
  sourceType = null,
  sourceId = null,
  accountingSourceId = null,
  sourceExternalKey = null,
  supplierId,
  supplierName,
  supplierSource,
  selectedSupplierOption,
  billId,
  onLinked,
}) {
  const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
  const headers = { headers: { accessToken: localStorage.getItem("accessToken") } };
  const normalizedPartyType = String(partyType || "supplier").trim().toLowerCase();
  const isShippingAccountParty = normalizedPartyType === "shipping-account";
  const isOtherServiceParty = normalizedPartyType === "other-service";
  const isCourierParty = String(sourceType || "").trim().toLowerCase() === "courier";
  const isDirectParty = isShippingAccountParty || isOtherServiceParty;
  const effectivePartyName = partyName || supplierName || "";

  const [supplierSearchTab, setSupplierSearchTab] = useState("core");
  const [supplierMarket, setSupplierMarket] = useState("GLOBAL");
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  const [qboQuery, setQboQuery] = useState(supplierName || "");
  const [qboLoading, setQboLoading] = useState(false);
  const [qboError, setQboError] = useState("");
  const [qboOptions, setQboOptions] = useState([]);
  const [selectedQbo, setSelectedQbo] = useState(null);
  const debounceRef = useRef();
  const lastAutoSelectQueryRef = useRef("");

  const [linking, setLinking] = useState(false);
  const [creating, setCreating] = useState(false);

  const getSupplierName = (supplier = {}) =>
    supplier?.Name || supplier?.name || supplier?.SupplierName || supplier?.businessName || "";

  const getSupplierSource = (supplier = {}) =>
    supplier?.supplierSource || supplier?.SupplierSource || supplierSource || (supplierSearchTab === "all" ? "asi-supplier" : "core-supplier");

  const getSupplierId = (supplier = {}) => supplier?.id || supplier?.Id || null;
  const parsedSourceExternalKey = parseSourceExternalKey(sourceExternalKey);
  const canonicalSourceExternalKey = isOtherServiceParty || isCourierParty
    ? (buildSourceExternalKey(sourceType, sourceId) || parsedSourceExternalKey.sourceExternalKey || "")
    : "";
  const getPartyId = () => (isOtherServiceParty || isCourierParty)
    ? (accountingSourceId ?? partyId ?? canonicalSourceExternalKey)
    : isShippingAccountParty ? partyId : getSupplierId(selectedSupplier);
  const getPartyName = () => isDirectParty ? effectivePartyName : getSupplierName(selectedSupplier);

  const buildSupplierPayload = (qboVendor) => {
    const resolvedSupplierId = getSupplierId(selectedSupplier);
    const resolvedSupplierSource = getSupplierSource(selectedSupplier);
    const resolvedSupplierName = getSupplierName(selectedSupplier) || supplierName || qboVendor?.label || "";

    return {
      instanceUniqueId,
      qboId: qboVendor.id,
      displayName: qboVendor.label,
      billId: billId || undefined,
      supplierId: resolvedSupplierId,
      supplierSource: resolvedSupplierSource,
      supplierName: resolvedSupplierName,
      temporaryAllowUnverifiedSupplier: true,
    };
  };

  const buildPartyPayload = (qboVendor) => ({
    instanceUniqueId,
    partyType: normalizedPartyType,
    partyId: getPartyId(),
    partyName: getPartyName(),
    sourceType: isOtherServiceParty || isCourierParty ? sourceType : undefined,
    sourceId: isOtherServiceParty || isCourierParty ? sourceId : undefined,
    sourceExternalKey: isOtherServiceParty || isCourierParty ? canonicalSourceExternalKey : undefined,
    qboId: qboVendor.id,
    displayName: qboVendor.label,
    billId: billId || undefined,
  });

  useEffect(() => {
    if (!open) return;
    if (isDirectParty) {
      setSupplierSearchTab("core");
      setSupplierMarket("GLOBAL");
      setSelectedSupplier(partyId ? {
        id: partyId,
        Id: partyId,
        Name: effectivePartyName,
      } : null);
      setSelectedQbo(null);
      setQboOptions([]);
      setQboError("");
      setQboQuery(effectivePartyName || "");
      lastAutoSelectQueryRef.current = "";
      return;
    }
    const nextTab = supplierSource === "asi-supplier" ? "all" : "core";
    setSupplierSearchTab(nextTab);
    setSupplierMarket("GLOBAL");
    setSelectedSupplier(selectedSupplierOption || (supplierId ? {
      id: supplierId,
      Id: supplierId,
      Name: supplierName || "",
      supplierSource: supplierSource || (nextTab === "all" ? "asi-supplier" : "core-supplier"),
      SupplierSource: supplierSource || (nextTab === "all" ? "asi-supplier" : "core-supplier"),
    } : null));
    setSelectedQbo(null);
    setQboOptions([]);
    setQboError("");
    setQboQuery(supplierName || "");
    lastAutoSelectQueryRef.current = "";
  }, [open, supplierId, supplierName, supplierSource, selectedSupplierOption, isDirectParty, partyId, effectivePartyName]);

  useEffect(() => {
    if (!open) return;
    setSelectedQbo(null);
    setQboOptions([]);
    setQboError("");
    lastAutoSelectQueryRef.current = "";

    const preferredName = isDirectParty ? effectivePartyName : getSupplierName(selectedSupplier);
    if (preferredName) setQboQuery(preferredName);
  }, [selectedSupplier, open, isDirectParty, effectivePartyName]);

  useEffect(() => {
    if (!open || !getPartyId()) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      try {
        setQboLoading(true);
        setQboError("");

        const trimmed = String(qboQuery || "").trim();
        const params = { instanceUniqueId };
        if (trimmed.length >= 2) params.q = trimmed;

        const { data } = await axios.get(`${SERVER}/suppliers/qbo/vendors/search`, {
          ...headers,
          params,
        });

        const normalized = (data?.results || []).map((r) => ({
          id: r.id,
          label: r.displayName,
          email: r.email || "",
          company: r.companyName || "",
        }));
        setQboOptions(normalized);

        const exactQuery = trimmed.toLowerCase();
        const exact = exactQuery
          ? normalized.find((opt) => {
              const label = String(opt?.label || "").trim().toLowerCase();
              const company = String(opt?.company || "").trim().toLowerCase();
              return label === exactQuery || company === exactQuery;
            })
          : null;
        const queryChanged = lastAutoSelectQueryRef.current !== trimmed;
        if (exact && (!selectedQbo || queryChanged)) {
          setSelectedQbo(exact);
          lastAutoSelectQueryRef.current = trimmed;
        }
      } catch (e) {
        console.error("Vendor search error", e?.response?.data || e);
        setQboOptions([]);
        setQboError(e?.response?.data?.error || "Search unavailable");
      } finally {
        setQboLoading(false);
      }
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [qboQuery, instanceUniqueId, open, selectedSupplier, SERVER, selectedQbo, isShippingAccountParty, partyId]);

  const handleLink = async () => {
    if (!getPartyId() || !selectedQbo?.id) return;
    try {
      setLinking(true);
      if (isDirectParty) {
        await axios.post(
          `${SERVER}/suppliers/qbo/vendor-links`,
          buildPartyPayload(selectedQbo),
          headers
        );
      } else {
        await axios.post(
          `${SERVER}/suppliers/${getSupplierId(selectedSupplier)}/qbo/link`,
          buildSupplierPayload(selectedQbo),
          headers
        );
      }
      onLinked?.();
      onClose?.();
    } catch (e) {
      console.error("Link vendor error", e?.response?.data || e);
      toast.error(e?.response?.data?.error || "Failed to link vendor to QuickBooks.");
    } finally {
      setLinking(false);
    }
  };

  const handleCreateInQbo = async () => {
    if (!getPartyId()) return;
    try {
      setCreating(true);
      const phone = isDirectParty ? null : selectedSupplier?.Phone;
      const primaryPhone = typeof phone === "string"
        ? phone
        : (phone?.Number || phone?.number || "");
      const typedName = String(qboQuery || "").trim();
      const displayName = typedName || getPartyName() || "Unnamed Vendor";

      const { data } = await axios.post(
        `${SERVER}/suppliers/qbo/vendors`,
        {
          instanceUniqueId,
          displayName,
          companyName: typedName || getPartyName() || "",
          primaryEmail: isDirectParty ? "" : (selectedSupplier?.Email || selectedSupplier?.email || ""),
          primaryPhone: primaryPhone || undefined,
        },
        headers
      );

      const vendor = data?.vendor || data?.Vendor || data || null;
      const createdQbo = {
        id: vendor?.id || vendor?.Id,
        label: vendor?.displayName || vendor?.DisplayName || displayName,
        email: vendor?.email || "",
        company: vendor?.companyName || "",
      };

      if (!createdQbo.id) throw new Error("QBO did not return a Vendor id");

      setSelectedQbo(createdQbo);
      if (isDirectParty) {
        await axios.post(
          `${SERVER}/suppliers/qbo/vendor-links`,
          buildPartyPayload(createdQbo),
          headers
        );
      } else {
        await axios.post(
          `${SERVER}/suppliers/${getSupplierId(selectedSupplier)}/qbo/link`,
          buildSupplierPayload(createdQbo),
          headers
        );
      }
      onLinked?.();
      onClose?.();
    } catch (e) {
      console.error("Create vendor error", e?.response?.data || e);
      toast.error(e?.response?.data?.error || e.message || "Create vendor failed");
    } finally {
      setCreating(false);
    }
  };

  const canCreate = !!getPartyId() && (!selectedQbo || qboOptions.length === 0);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Link QuickBooks Vendor</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {isDirectParty
            ? `Search and connect this ${isOtherServiceParty ? "service provider" : "shipping account"} to a QuickBooks vendor.`
            : "Pick an Elite supplier, then search and connect to a QuickBooks vendor."}
        </Typography>

        {isDirectParty ? (
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Billing Party"
              value={effectivePartyName || ""}
              InputProps={{ readOnly: true }}
            />
          </Box>
        ) : (
        <Box sx={{ mb: 2 }}>
          <Tabs
            value={supplierSearchTab}
            onChange={(_, nextTab) => {
              setSupplierSearchTab(nextTab);
              if (nextTab === "core") setSupplierMarket("GLOBAL");
              setSelectedSupplier(null);
            }}
            sx={{ mb: 1 }}
          >
            <Tab value="core" label="Internal Supplier" />
            <Tab value="all" label="Supplier" />
          </Tabs>

          {supplierSearchTab === "all" && (
            <Box sx={{ display: "flex", gap: 0.75, mt: 1, mb: 1, alignItems: "center" }}>
              <Chip
                size="small"
                label="CAN"
                onClick={() => setSupplierMarket("CANALL")}
                sx={{
                  fontWeight: 700,
                  backgroundColor: supplierMarket === "CANALL" ? "primary.main" : "grey.100",
                  color: supplierMarket === "CANALL" ? "primary.contrastText" : "text.primary",
                  border: "1px solid",
                  borderColor: supplierMarket === "CANALL" ? "primary.main" : "divider",
                }}
              />
              <Chip
                size="small"
                label="ALL"
                onClick={() => setSupplierMarket("GLOBAL")}
                sx={{
                  fontWeight: 700,
                  backgroundColor: supplierMarket === "GLOBAL" ? "primary.main" : "grey.100",
                  color: supplierMarket === "GLOBAL" ? "primary.contrastText" : "text.primary",
                  border: "1px solid",
                  borderColor: supplierMarket === "GLOBAL" ? "primary.main" : "divider",
                }}
              />
            </Box>
          )}

          <SupplierSearchSelectAsync
            key={`${supplierSearchTab}-${supplierMarket}`}
            instanceUniqueId={instanceUniqueId}
            value={getSupplierId(selectedSupplier) || null}
            selectedSupplierOption={selectedSupplier}
            tab={supplierSearchTab}
            searchMode={supplierSearchTab}
            market={supplierMarket}
            onChange={(id) => { if (!id) setSelectedSupplier(null); }}
            onSelectSupplier={(supplier) => setSelectedSupplier(supplier || null)}
            label={supplierSearchTab === "core" ? "Internal Supplier" : "Supplier"}
            placeholder={supplierSearchTab === "core" ? "Search internal suppliers..." : "Search suppliers..."}
            helperText={!getSupplierId(selectedSupplier) ? "Select the supplier to link." : ""}
            error={!getSupplierId(selectedSupplier)}
          />
        </Box>
        )}

        <Autocomplete
          options={qboOptions}
          value={selectedQbo}
          disabled={!getPartyId()}
          loading={qboLoading}
          onChange={(_, value) => setSelectedQbo(value || null)}
          onInputChange={(_, value, reason) => {
            if (reason === "input") setQboQuery(value);
          }}
          getOptionLabel={(opt) => opt?.label || ""}
          isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
          loadingText="Searching…"
          noOptionsText={
            !getPartyId()
              ? (isShippingAccountParty ? "Missing shipping account" : "Pick an Elite supplier first")
              : qboError
                ? "Search unavailable"
                : "No QuickBooks matches"
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="QuickBooks Vendor"
              size="small"
              placeholder="Search QuickBooks vendors"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {qboLoading ? <CircularProgress size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, opt) => (
            <li {...props} key={opt.id}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="body2">{opt.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {[opt.email, opt.company].filter(Boolean).join(" • ") || "Vendor"}
                </Typography>
              </Box>
            </li>
          )}
          sx={{ mb: 2 }}
        />

        {!!qboError && <Alert severity="error" sx={{ mb: 2 }}>{qboError}</Alert>}

        {canCreate && (
          <Box sx={{ mb: 1 }}>
            <Button variant="outlined" onClick={handleCreateInQbo} disabled={creating}>
              {creating
                ? "Creating…"
                : (String(qboQuery || "").trim()
                  ? `Create "${String(qboQuery || "").trim()}" in QuickBooks`
                  : "Create in QuickBooks")}
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">Close</Button>
        <Button
          onClick={handleLink}
          variant="contained"
          disabled={!getPartyId() || !selectedQbo?.id || linking}
        >
          {linking ? "Connecting…" : "Connect"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
